import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { searchRepository } from '@/modules/search/services/search-repository'
import type {
  ConnectorProgress,
  NormalizedProduct,
  SearchFilters,
  SearchHistoryRecord,
  SearchSortBy,
  SearchStatus,
  SearchSummary,
} from '@/modules/search/types'
import { createDefaultFilters } from '@/modules/search/utils'
import { zustandStorage } from '@/services/storage'

interface SearchState {
  term: string
  status: SearchStatus
  products: NormalizedProduct[]
  summary: SearchSummary
  progress: ConnectorProgress[]
  filters: SearchFilters
  sortBy: SearchSortBy
  executionTime: number
  error?: string
  history: SearchHistoryRecord[]
  favorites: string[]
  recentTerms: string[]

  setTerm: (term: string) => void
  setFilters: (filters: Partial<SearchFilters>) => void
  setSortBy: (sortBy: SearchSortBy) => void
  setSearching: () => void
  setProgress: (progress: ConnectorProgress[]) => void
  setResult: (payload: {
    products: NormalizedProduct[]
    summary: SearchSummary
    progress: ConnectorProgress[]
    executionTime: number
    status: Exclude<SearchStatus, 'idle' | 'searching'>
    error?: string
    term: string
  }) => void
  resetResults: () => void
  refreshHistory: () => void
  toggleFavorite: (term: string) => void
  clearHistory: () => void
}

const emptySummary: SearchSummary = {
  totalFound: 0,
  minPrice: null,
  maxPrice: null,
  avgPrice: null,
  cheapestSupplier: null,
}

export const useSearchStore = create<SearchState>()(
  persist(
    (set, get) => ({
      term: '',
      status: 'idle',
      products: [],
      summary: emptySummary,
      progress: [],
      filters: createDefaultFilters(),
      sortBy: 'price_asc',
      executionTime: 0,
      error: undefined,
      history: [],
      favorites: [],
      recentTerms: [],

      setTerm: (term) => set({ term }),

      setFilters: (filters) =>
        set({ filters: { ...get().filters, ...filters } }),

      setSortBy: (sortBy) => set({ sortBy }),

      setSearching: () =>
        set({ status: 'searching', error: undefined, progress: [] }),

      setProgress: (progress) => set({ progress }),

      setResult: ({ products, summary, progress, executionTime, status, error, term }) => {
        const recentTerms = [term, ...get().recentTerms.filter((t) => t !== term)].slice(0, 12)
        set({
          products,
          summary,
          progress,
          executionTime,
          status,
          error,
          recentTerms,
          history: searchRepository.listHistory(),
        })
      },

      resetResults: () =>
        set({
          products: [],
          summary: emptySummary,
          progress: [],
          status: 'idle',
          error: undefined,
          executionTime: 0,
        }),

      refreshHistory: () =>
        set({
          history: searchRepository.listHistory(),
          favorites: searchRepository.listFavorites(),
        }),

      toggleFavorite: (term) =>
        set({ favorites: searchRepository.toggleFavorite(term) }),

      clearHistory: () => {
        searchRepository.clearHistory()
        set({ history: [] })
      },
    }),
    {
      name: 'masterhub-search-store',
      storage: zustandStorage,
      partialize: (state) => ({
        recentTerms: state.recentTerms,
        filters: {
          ...state.filters,
          suppliers: state.filters.suppliers,
          inStockOnly: state.filters.inStockOnly,
        },
        sortBy: state.sortBy,
      }),
    },
  ),
)
